import React, {useEffect, useMemo, useState} from 'react';
import {StyleSheet, css} from 'aphrodite';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import {FiCreditCard, FiEdit2, FiFileText, FiSettings, FiShield, FiShoppingCart, FiTrash2} from 'react-icons/fi';
import {getOrders, getProfile} from '../api/api';
import {getProfilePhoto} from '../utils/profilePhoto';

import {
  CONTACT_DETAILS,
  ABOUT_APP_TEXT,
  PRICING_DATA,
  KYC_DATA,
  EDD_INTRO,
  EDD_POINTS,
  ACCEPTABLE_USE_INTRO,
  ACCEPTABLE_USE_POINTS,
  ACCEPTABLE_USE_OUTRO,
  PAYMENT_FLOW_INTRO,
  PAYMENT_FLOW_STEPS,
  PAYMENT_FLOW_OUTRO,
  DISPUTE_INTRO,
  DISPUTE_STEPS,
  DISPUTE_OUTRO,
  TERMS_USE_INTRO,
  TERMS_USE_POINTS,
  PRIVACY_POLICY_TEXT,
  FAQ_DATA,
} from '../data/yabathoLegalContent';

const logo = '/logo192.png';

const getStatusColor = status => {
  switch (String(status || '').toLowerCase()) {
    case 'delivered':
    case 'completed':
      return '#27AE60';
    case 'on the way':
    case 'picked up':
      return '#2D9CDB';
    case 'preparing':
      return '#C9A14A';
    case 'cancelled':
    case 'canceled':
      return '#D9534F';
    case 'ready for delivery':
      return '#8E5BBE';
    default:
      return '#0F3D2E';
  }
};

const getOrderTotal = order => {
  const directTotal = Number(order?.totalPrice);

  if (!Number.isNaN(directTotal) && directTotal > 0) {
    return directTotal;
  }

  if (Array.isArray(order?.orderItems)) {
    return order.orderItems.reduce((sum, item) => {
      const rawPrice = item.priceAtOrder ?? item.menuItem?.price ?? 0;
      const numericPrice = Number(String(rawPrice).replace(',', '.')) || 0;
      return sum + numericPrice * (item.quantity || 0);
    }, 0);
  }

  return 0;
};

const Profile = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [activeModal, setActiveModal] = useState(null);
  const [profileImageFailed, setProfileImageFailed] = useState(false);
  const [accountLoading, setAccountLoading] = useState(true);
  const [accountError, setAccountError] = useState('');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    let storedUser = null;

    if (userData) {
      try {
        storedUser = JSON.parse(userData);
        setUser(storedUser);
      } catch (error) {
        console.error('Failed to parse user data:', error);
      }
    }

    const storedOrders = localStorage.getItem('orders');

    if (storedOrders) {
      try {
        setOrders(JSON.parse(storedOrders));
      } catch (error) {
        console.error('Failed to parse stored orders:', error);
      }
    }

    const loadAccountData = async () => {
      const token = localStorage.getItem('token');
      if (!token || token === 'null') {
        setAccountLoading(false);
        return;
      }

      try {
        setAccountError('');
        const [databaseUser, databaseOrders] = await Promise.all([
          getProfile(),
          getOrders(),
        ]);

        if (databaseUser) {
          const syncedUser = {...storedUser, ...databaseUser};
          setUser(syncedUser);
          localStorage.setItem('user', JSON.stringify(syncedUser));
        }

        const syncedOrders = Array.isArray(databaseOrders) ? databaseOrders : [];
        setOrders(syncedOrders);
        localStorage.setItem('orders', JSON.stringify(syncedOrders));
      } catch (error) {
        setAccountError(error?.message || 'Unable to refresh your account data.');
      } finally {
        setAccountLoading(false);
      }
    };

    loadAccountData();
  }, []);

  const recentOrders = useMemo(() => {
    return orders
      .map(order => ({
        ...order,
        total: getOrderTotal(order),
        date: order.createdAt ? new Date(order.createdAt) : new Date(),
        id: order.id || order._id || Math.random().toString(),
      }))
      .sort((a, b) => b.date - a.date)
      .slice(0, 3);
  }, [orders]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('orders');
    setActiveModal(null);
    navigate('/');
  };

  const fullName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
    : '';

  const displayName = fullName || user?.name || 'Guest';
  const displayEmail = user?.email || 'Not signed in';
  const profilePhoto = getProfilePhoto(user);

  useEffect(() => {
    setProfileImageFailed(false);
  }, [profilePhoto]);

  const firstInitial =
    user?.firstName?.charAt(0)?.toUpperCase() ||
    user?.email?.charAt(0)?.toUpperCase() ||
    'Y';

  const SettingRow = ({icon, label, onClick, danger = false, last = false}) => {
    return (
      <button
        type="button"
        className={css(styles.settingRow, last && styles.lastRow)}
        onClick={onClick}>
        <span className={css(styles.settingIcon, danger && styles.dangerIcon)}>
          {icon}
        </span>

        <span className={css(styles.settingLabel, danger && styles.dangerText)}>
          {label}
        </span>

        <span className={css(styles.chevron)}>›</span>
      </button>
    );
  };

  const ModalParagraphs = ({items}) => {
    return items.map((item, index) => (
      <p key={`${item}-${index}`} className={css(styles.modalText)}>
        {item}
      </p>
    ));
  };

  const ModalList = ({items}) => {
    return (
      <ul className={css(styles.modalList)}>
        {items.map((item, index) => (
          <li key={`${item}-${index}`}>{item}</li>
        ))}
      </ul>
    );
  };

  const ModalSection = ({title, children}) => {
    return (
      <div className={css(styles.modalSection)}>
        <h4 className={css(styles.modalSectionTitle)}>{title}</h4>
        {children}
      </div>
    );
  };

  const closeModal = () => setActiveModal(null);

  return (
    <div className={css(styles.page)}>
      <main className={css(styles.shell)}>
        <section className={css(styles.heroCard)}>
          <div className={css(styles.heroTopRow)}>
            <div>
              <p className={css(styles.kicker)}>YaBatho Profile</p>
              <h1 className={css(styles.heroTitle)}>Account</h1>
            </div>

            <div className={css(styles.ybBadge)}>
              <img
                src={logo}
                alt="YaBatho Logo"
                className={css(styles.ybLogoImage)}
              />
            </div>
          </div>

          <div className={css(styles.profileArea)}>
            <div className={css(styles.profileFallback)}>
              {profilePhoto && !profileImageFailed ? (
                <img
                  src={profilePhoto}
                  alt={`${displayName} profile`}
                  className={css(styles.profileImage)}
                  referrerPolicy="no-referrer"
                  onError={() => setProfileImageFailed(true)}
                />
              ) : firstInitial}
            </div>

            <div className={css(styles.profileTextWrap)}>
              <h2 className={css(styles.name)}>Hello, {displayName}</h2>
              <p className={css(styles.email)}>{displayEmail}</p>
            </div>
          </div>

          <div className={css(styles.profileMetaCard)}>
            <div className={css(styles.profileMetaItem)}>
              <span aria-hidden="true"><FiShield /></span>
              <strong>Secure account</strong>
            </div>

            <div className={css(styles.profileMetaDivider)} />

            <div className={css(styles.profileMetaItem)}>
              <span aria-hidden="true"><FiShoppingCart /></span>
              <strong>
                {accountLoading
                  ? 'Loading orders...'
                  : `${orders.length} order${orders.length === 1 ? '' : 's'}`}
              </strong>
            </div>
          </div>

          {accountError ? (
            <p className={css(styles.accountError)} role="alert">{accountError}</p>
          ) : null}
        </section>

        <section className={css(styles.grid)}>
          <div className={css(styles.card)}>
            <div className={css(styles.sectionHeader)}>
              <div>
                <p className={css(styles.sectionKicker)}>Activity</p>
                <h2 className={css(styles.sectionTitle)}>Recent Orders</h2>
              </div>

              <span className={css(styles.sectionIcon)}><FiFileText /></span>
            </div>

            {recentOrders.length > 0 ? (
              <>
                <div className={css(styles.ordersList)}>
                  {recentOrders.map(order => (
                    <div key={order.id} className={css(styles.orderRow)}>
                      <div className={css(styles.orderLeft)}>
                        <p className={css(styles.orderRestaurant)}>
                          {order.restaurant?.name ||
                            order.restaurantName ||
                            order.outlet ||
                            'Restaurant'}
                        </p>

                        <p className={css(styles.orderDate)}>
                          {moment(order.date).format('DD MMM YYYY, HH:mm')}
                        </p>
                      </div>

                      <div className={css(styles.orderRight)}>
                        <p
                          className={css(styles.orderStatus)}
                          style={{color: getStatusColor(order.status)}}>
                          {order.status || 'Pending'}
                        </p>

                        <p className={css(styles.orderTotal)}>
                          R {order.total.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className={css(styles.viewAllButton)}
                  onClick={() => navigate('/orders')}>
                  View all orders
                </button>
              </>
            ) : (
              <div className={css(styles.emptyOrders)}>
                <div className={css(styles.emptyIcon)}><FiShoppingCart /></div>
                <h3>No recent orders</h3>
                <p>Your latest YaBatho orders will appear here.</p>
              </div>
            )}
          </div>

          <div className={css(styles.card)}>
            <div className={css(styles.sectionHeader)}>
              <div>
                <p className={css(styles.sectionKicker)}>Controls</p>
                <h2 className={css(styles.sectionTitle)}>Settings</h2>
              </div>

              <span className={css(styles.sectionIcon)}><FiSettings /></span>
            </div>

            <SettingRow
              icon={<FiCreditCard />}
              label="YaBatho Wallet"
              onClick={() => navigate('/wallet')}
            />

            <SettingRow
              icon={<FiEdit2 />}
              label="Edit Profile"
              onClick={() => navigate('/edit-profile')}
            />

            <SettingRow
              icon="🔒"
              label="Change Password"
              onClick={() => navigate('/change-password')}
              last
            />
          </div>
        </section>

        <section className={css(styles.card)}>
          <div className={css(styles.sectionHeader)}>
            <div>
              <p className={css(styles.sectionKicker)}>Information</p>
              <h2 className={css(styles.sectionTitle)}>Support & Policies</h2>
            </div>

            <span className={css(styles.sectionIcon)}>📄</span>
          </div>

          <div className={css(styles.settingsGrid)}>
            <SettingRow
              icon="🆘"
              label="Help & Support"
              onClick={() => navigate('/support')}
            />

            <SettingRow
              icon="ℹ️"
              label="About This App"
              onClick={() => setActiveModal('about')}
            />

            <SettingRow
              icon="💰"
              label="Pricing"
              onClick={() => setActiveModal('pricing')}
            />

            <SettingRow
              icon={<FiFileText />}
              label="KYC Information"
              onClick={() => setActiveModal('kyc')}
            />

            <SettingRow
              icon="🔍"
              label="Enhanced Due Diligence"
              onClick={() => setActiveModal('edd')}
            />

            <SettingRow
              icon="✅"
              label="Acceptable Use Policy"
              onClick={() => setActiveModal('acceptableUse')}
            />

            <SettingRow
              icon={<FiCreditCard />}
              label="Payment Flow"
              onClick={() => setActiveModal('paymentFlow')}
            />

            <SettingRow
              icon="⚖️"
              label="Dispute Management"
              onClick={() => setActiveModal('disputes')}
            />

            <SettingRow
              icon={<FiShield />}
              label="Privacy Policy"
              onClick={() => setActiveModal('privacy')}
            />

            <SettingRow
              icon={<FiTrash2 />}
              label="Delete Account"
              onClick={() => navigate('/delete-account')}
            />

            <SettingRow
              icon="📄"
              label="Terms & Conditions"
              onClick={() => setActiveModal('terms')}
            />

            <SettingRow
              icon="❓"
              label="FAQ"
              onClick={() => setActiveModal('faq')}
              last
            />
          </div>
        </section>

        <button
          type="button"
          className={css(styles.logoutButton)}
          onClick={() => setActiveModal('logout')}>
          <span>↪</span>
          Logout
        </button>

        <section className={css(styles.footerCard)}>
          <p className={css(styles.version)}>YaBatho Customer Web App</p>
          <p className={css(styles.footerTagline)}>
            For the people. Built for local life.
          </p>
          <p className={css(styles.copyright)}>
            © {new Date().getFullYear()} C Digital & LPTB Solutions
          </p>
        </section>
      </main>

      {activeModal && (
        <div className={css(styles.modalOverlay)} onClick={closeModal}>
          <div
            className={css(styles.modalContainer)}
            onClick={event => event.stopPropagation()}>
            <button
              type="button"
              className={css(styles.modalCloseX)}
              onClick={closeModal}
              aria-label="Close modal">
              ×
            </button>

            {activeModal === 'logout' && (
              <>
                <div className={css(styles.modalIconDanger)}>↪</div>
                <h3 className={css(styles.modalTitle)}>Logout</h3>
                <p className={css(styles.modalText)}>
                  Are you sure you want to logout from your YaBatho account?
                </p>

                <div className={css(styles.modalButtons)}>
                  <button
                    type="button"
                    className={css(styles.cancelButton)}
                    onClick={closeModal}>
                    Cancel
                  </button>

                  <button
                    type="button"
                    className={css(styles.confirmButton)}
                    onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              </>
            )}

            {activeModal === 'about' && (
              <>
                <h3 className={css(styles.modalTitle)}>About YaBatho</h3>
                <ModalParagraphs items={ABOUT_APP_TEXT} />

                <ModalSection title="Platform">
                  <p className={css(styles.modalText)}>
                    YaBatho means “for the people”. The platform is built to
                    connect local customers, restaurants, riders, and businesses
                    through a modern delivery ecosystem.
                  </p>
                </ModalSection>

                <ModalSection title="Version">
                  <p className={css(styles.modalText)}>Version: 1.2.27</p>
                  <p className={css(styles.modalText)}>
                    Developed by C Digital & LPTB Solutions.
                  </p>
                </ModalSection>

                <button
                  type="button"
                  className={css(styles.closeModalButton)}
                  onClick={closeModal}>
                  Close
                </button>
              </>
            )}

            {activeModal === 'help' && (
              <>
                <h3 className={css(styles.modalTitle)}>Help & Support</h3>

                <p className={css(styles.modalText)}>
                  Need help with your account, order, payment, or delivery?
                </p>

                <div className={css(styles.contactCard)}>
                  <p>
                    <strong>Email:</strong> {CONTACT_DETAILS.supportEmail}
                  </p>
                  <p>
                    <strong>WhatsApp:</strong> {CONTACT_DETAILS.whatsapp}
                  </p>
                  <p>
                    <strong>Address:</strong> {CONTACT_DETAILS.address}
                  </p>
                </div>

                <button
                  type="button"
                  className={css(styles.closeModalButton)}
                  onClick={closeModal}>
                  Close
                </button>
              </>
            )}

            {activeModal === 'pricing' && (
              <>
                <h3 className={css(styles.modalTitle)}>Pricing</h3>

                <ModalSection title="Customer Pricing">
                  <ModalList items={PRICING_DATA.customer} />
                </ModalSection>

                <ModalSection title="Restaurant Pricing">
                  <ModalList items={PRICING_DATA.restaurant} />
                </ModalSection>

                <ModalSection title="Rider Earnings">
                  <ModalList items={PRICING_DATA.rider} />
                </ModalSection>

                <button
                  type="button"
                  className={css(styles.closeModalButton)}
                  onClick={closeModal}>
                  Close
                </button>
              </>
            )}

            {activeModal === 'kyc' && (
              <>
                <h3 className={css(styles.modalTitle)}>KYC Information</h3>

                <ModalSection title="Customers">
                  <ModalList items={KYC_DATA.customers} />
                </ModalSection>

                <ModalSection title="Restaurants">
                  <ModalList items={KYC_DATA.restaurants} />
                </ModalSection>

                <ModalSection title="Riders">
                  <ModalList items={KYC_DATA.riders} />
                </ModalSection>

                <button
                  type="button"
                  className={css(styles.closeModalButton)}
                  onClick={closeModal}>
                  Close
                </button>
              </>
            )}

            {activeModal === 'edd' && (
              <>
                <h3 className={css(styles.modalTitle)}>
                  Enhanced Due Diligence
                </h3>

                <p className={css(styles.modalText)}>{EDD_INTRO}</p>
                <ModalList items={EDD_POINTS} />

                <button
                  type="button"
                  className={css(styles.closeModalButton)}
                  onClick={closeModal}>
                  Close
                </button>
              </>
            )}

            {activeModal === 'acceptableUse' && (
              <>
                <h3 className={css(styles.modalTitle)}>
                  Acceptable Use Policy
                </h3>

                <ModalParagraphs items={ACCEPTABLE_USE_INTRO} />
                <ModalList items={ACCEPTABLE_USE_POINTS} />
                <p className={css(styles.modalText)}>{ACCEPTABLE_USE_OUTRO}</p>

                <button
                  type="button"
                  className={css(styles.closeModalButton)}
                  onClick={closeModal}>
                  Close
                </button>
              </>
            )}

            {activeModal === 'paymentFlow' && (
              <>
                <h3 className={css(styles.modalTitle)}>Payment Flow</h3>

                <p className={css(styles.modalText)}>{PAYMENT_FLOW_INTRO}</p>
                <ModalList items={PAYMENT_FLOW_STEPS} />
                <p className={css(styles.modalText)}>{PAYMENT_FLOW_OUTRO}</p>

                <button
                  type="button"
                  className={css(styles.closeModalButton)}
                  onClick={closeModal}>
                  Close
                </button>
              </>
            )}

            {activeModal === 'disputes' && (
              <>
                <h3 className={css(styles.modalTitle)}>
                  Dispute Management
                </h3>

                <p className={css(styles.modalText)}>{DISPUTE_INTRO}</p>
                <ModalList items={DISPUTE_STEPS} />
                <p className={css(styles.modalText)}>{DISPUTE_OUTRO}</p>

                <button
                  type="button"
                  className={css(styles.closeModalButton)}
                  onClick={closeModal}>
                  Close
                </button>
              </>
            )}

            {activeModal === 'privacy' && (
              <>
                <h3 className={css(styles.modalTitle)}>Privacy Policy</h3>

                <div className={css(styles.longTextBlock)}>
                  {PRIVACY_POLICY_TEXT.split('\n').map((line, index) => {
                    const trimmedLine = line.trim();

                    if (!trimmedLine) {
                      return null;
                    }

                    const isHeading = /^\d+\./.test(trimmedLine);

                    return isHeading ? (
                      <h4
                        key={`${trimmedLine}-${index}`}
                        className={css(styles.privacyHeading)}>
                        {trimmedLine}
                      </h4>
                    ) : (
                      <p
                        key={`${trimmedLine}-${index}`}
                        className={css(styles.modalText)}>
                        {trimmedLine}
                      </p>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className={css(styles.closeModalButton)}
                  onClick={closeModal}>
                  Close
                </button>
              </>
            )}

            {activeModal === 'terms' && (
              <>
                <h3 className={css(styles.modalTitle)}>Terms & Conditions</h3>

                <ModalParagraphs items={TERMS_USE_INTRO} />

                {TERMS_USE_POINTS.map((item, index) => {
                  const nextItem = TERMS_USE_POINTS[index + 1];
                  const isHeading =
                    nextItem &&
                    !item.includes('.') &&
                    item.length < 40 &&
                    index !== 0;

                  if (isHeading) {
                    return (
                      <h4
                        key={`${item}-${index}`}
                        className={css(styles.privacyHeading)}>
                        {item}
                      </h4>
                    );
                  }

                  return (
                    <p key={`${item}-${index}`} className={css(styles.modalText)}>
                      {item}
                    </p>
                  );
                })}

                <button
                  type="button"
                  className={css(styles.closeModalButton)}
                  onClick={closeModal}>
                  Close
                </button>
              </>
            )}

            {activeModal === 'faq' && (
              <>
                <h3 className={css(styles.modalTitle)}>FAQ</h3>

                {FAQ_DATA.map((item, index) => (
                  <div key={`${item.question}-${index}`} className={css(styles.faqItem)}>
                    <h4>{item.question}</h4>
                    <p>{item.answer}</p>
                  </div>
                ))}

                <button
                  type="button"
                  className={css(styles.closeModalButton)}
                  onClick={closeModal}>
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = StyleSheet.create({
  page: {
    minHeight: '100vh',
    width: '100%',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
    padding: '24px 24px 120px',
    boxSizing: 'border-box',

    '@media (max-width: 720px)': {
      padding: '16px 14px 112px',
    },
  },

  shell: {
    width: '100%',
    maxWidth: '1050px',
    margin: '0 auto',
  },

  heroCard: {
    backgroundColor: '#0F3D2E',
    borderRadius: '32px',
    padding: '26px',
    marginBottom: '20px',
    boxShadow: '0 22px 50px rgba(15, 61, 46, 0.16)',
    border: '1px solid rgba(201, 161, 74, 0.45)',
  },

  heroTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '22px',
    gap: '16px',
  },

  kicker: {
    margin: '0 0 6px',
    color: '#C9A14A',
    fontSize: '12px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },

  heroTitle: {
    margin: 0,
    color: '#ffffff',
    fontSize: '36px',
    fontWeight: '900',
    letterSpacing: '-1.4px',

    '@media (max-width: 520px)': {
      fontSize: '30px',
    },
  },

  ybBadge: {
    width: '78px',
    height: '78px',
    minWidth: '78px',
    borderRadius: '50%',
    border: '2px solid #C9A14A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    boxShadow: '0 14px 30px rgba(0, 0, 0, 0.14)',

    '@media (max-width: 520px)': {
      width: '68px',
      height: '68px',
      minWidth: '68px',
    },
  },

  ybLogoImage: {
    width: '62px',
    height: '62px',
    objectFit: 'contain',
    display: 'block',

    '@media (max-width: 520px)': {
      width: '54px',
      height: '54px',
    },
  },

  profileArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',

    '@media (max-width: 520px)': {
      alignItems: 'flex-start',
    },
  },

  profileFallback: {
    width: '82px',
    height: '82px',
    minWidth: '82px',
    borderRadius: '50%',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    border: '2px solid #C9A14A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '30px',
    fontWeight: '900',
    overflow: 'hidden',
  },

  profileImage: {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'cover',
  },

  profileTextWrap: {
    minWidth: 0,
  },

  name: {
    margin: 0,
    color: '#ffffff',
    fontSize: '24px',
    fontWeight: '900',
    lineHeight: 1.25,
  },

  email: {
    margin: '6px 0 0',
    color: '#F4EBD8',
    fontSize: '14px',
    wordBreak: 'break-word',
  },

  profileMetaCard: {
    marginTop: '22px',
    backgroundColor: 'rgba(255, 253, 247, 0.08)',
    borderRadius: '22px',
    border: '1px solid rgba(201, 161, 74, 0.35)',
    padding: '14px',
    display: 'flex',
    alignItems: 'center',

    '@media (max-width: 520px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '12px',
    },
  },

  profileMetaItem: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#F4EBD8',
    fontSize: '13px',
  },

  accountError: {
    margin: '14px 0 0',
    color: '#FFD5D5',
    fontSize: '13px',
    fontWeight: '700',
  },

  profileMetaDivider: {
    width: '1px',
    height: '30px',
    backgroundColor: 'rgba(230, 216, 190, 0.35)',
    margin: '0 14px',

    '@media (max-width: 520px)': {
      display: 'none',
    },
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '18px',
    marginBottom: '18px',

    '@media (max-width: 860px)': {
      gridTemplateColumns: '1fr',
    },
  },

  card: {
    border: '1px solid #E6D8BE',
    borderRadius: '28px',
    padding: '18px',
    marginBottom: '18px',
    backgroundColor: '#ffffff',
    boxShadow: '0 12px 34px rgba(30, 30, 30, 0.06)',
    boxSizing: 'border-box',
  },

  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: '14px',
    marginBottom: '4px',
    borderBottom: '1px solid #F1E7D2',
    gap: '12px',
  },

  sectionKicker: {
    margin: '0 0 4px',
    color: '#C9A14A',
    fontSize: '11px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
  },

  sectionTitle: {
    margin: 0,
    color: '#0F3D2E',
    fontSize: '20px',
    fontWeight: '900',
  },

  sectionIcon: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    backgroundColor: '#0F3D2E',
    color: '#C9A14A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  ordersList: {
    paddingTop: '8px',
  },

  orderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '13px 0',
    borderBottom: '1px solid #F1E7D2',

    ':last-child': {
      borderBottom: 'none',
    },
  },

  orderLeft: {
    minWidth: 0,
  },

  orderRight: {
    textAlign: 'right',
    minWidth: '90px',
  },

  orderRestaurant: {
    margin: 0,
    color: '#0F3D2E',
    fontSize: '14px',
    fontWeight: '900',
    wordBreak: 'break-word',
  },

  orderDate: {
    margin: '5px 0 0',
    color: '#8C8170',
    fontSize: '12px',
  },

  orderStatus: {
    margin: 0,
    fontSize: '12px',
    fontWeight: '900',
    textTransform: 'capitalize',
  },

  orderTotal: {
    margin: '5px 0 0',
    color: '#C9A14A',
    fontSize: '13px',
    fontWeight: '900',
  },

  viewAllButton: {
    width: '100%',
    marginTop: '14px',
    padding: '13px 16px',
    borderRadius: '18px',
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    fontSize: '14px',
    fontWeight: '900',
    cursor: 'pointer',
  },

  emptyOrders: {
    padding: '28px 16px 18px',
    textAlign: 'center',

    h3: {
      margin: '0 0 6px',
      color: '#0F3D2E',
      fontSize: '18px',
    },

    p: {
      margin: 0,
      color: '#6E6254',
      fontSize: '13px',
      lineHeight: 1.6,
    },
  },

  emptyIcon: {
    width: '58px',
    height: '58px',
    borderRadius: '50%',
    backgroundColor: '#F4EBD8',
    border: '1px solid #E6D8BE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 12px',
  },

  settingsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
  },

  settingRow: {
    width: '100%',
    border: 'none',
    borderBottom: '1px solid #F1E7D2',
    backgroundColor: 'transparent',
    padding: '14px 0',
    display: 'flex',
    alignItems: 'center',
    textAlign: 'left',
    cursor: 'pointer',
  },

  lastRow: {
    borderBottom: 'none',
  },

  settingIcon: {
    width: '40px',
    height: '40px',
    minWidth: '40px',
    borderRadius: '50%',
    backgroundColor: '#0F3D2E',
    color: '#C9A14A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
  },

  dangerIcon: {
    backgroundColor: '#FFE9E9',
    color: '#D9534F',
  },

  settingLabel: {
    flex: 1,
    color: '#0F3D2E',
    fontSize: '15px',
    fontWeight: '900',
  },

  dangerText: {
    color: '#D9534F',
  },

  chevron: {
    color: '#8C8170',
    fontSize: '24px',
    fontWeight: '700',
  },

  logoutButton: {
    width: '100%',
    border: 'none',
    backgroundColor: '#D9534F',
    color: '#ffffff',
    padding: '15px',
    borderRadius: '22px',
    fontSize: '15px',
    fontWeight: '900',
    cursor: 'pointer',
    marginBottom: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 10px 24px rgba(217, 83, 79, 0.18)',
  },

  footerCard: {
    alignItems: 'center',
    backgroundColor: '#F4EBD8',
    borderRadius: '24px',
    padding: '18px',
    border: '1px solid #E6D8BE',
    textAlign: 'center',
  },

  version: {
    margin: 0,
    color: '#0F3D2E',
    fontSize: '14px',
    fontWeight: '900',
  },

  footerTagline: {
    margin: '5px 0 0',
    color: '#6E6254',
    fontSize: '13px',
  },

  copyright: {
    margin: '7px 0 0',
    color: '#8C8170',
    fontSize: '12px',
  },

  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 61, 46, 0.58)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    zIndex: 1200,
    boxSizing: 'border-box',
  },

  modalContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: '620px',
    maxHeight: '86vh',
    overflowY: 'auto',
    backgroundColor: '#FFFDF7',
    borderRadius: '30px',
    padding: '26px',
    border: '1px solid #E6D8BE',
    boxShadow: '0 22px 50px rgba(30, 30, 30, 0.2)',
    boxSizing: 'border-box',
    textAlign: 'left',

    '@media (max-width: 520px)': {
      padding: '22px 18px',
      borderRadius: '24px',
    },
  },

  modalCloseX: {
    position: 'absolute',
    top: '14px',
    right: '16px',
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    border: '1px solid #E6D8BE',
    backgroundColor: '#ffffff',
    color: '#0F3D2E',
    fontSize: '24px',
    fontWeight: '800',
    cursor: 'pointer',
    lineHeight: 1,
  },

  modalIconDanger: {
    width: '58px',
    height: '58px',
    borderRadius: '50%',
    backgroundColor: '#FFE9E9',
    border: '1px solid #F5B8B8',
    color: '#D9534F',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto 14px',
    fontSize: '24px',
    fontWeight: '900',
  },

  modalTitle: {
    margin: '0 44px 14px 0',
    color: '#0F3D2E',
    fontSize: '24px',
    fontWeight: '900',
    letterSpacing: '-0.5px',
  },

  modalText: {
    margin: '0 0 13px',
    color: '#6E6254',
    fontSize: '14px',
    lineHeight: 1.75,
  },

  modalSection: {
    marginTop: '18px',
    paddingTop: '16px',
    borderTop: '1px solid #F1E7D2',
  },

  modalSectionTitle: {
    margin: '0 0 10px',
    color: '#0F3D2E',
    fontSize: '15px',
    fontWeight: '900',
  },

  modalList: {
    margin: '0 0 8px',
    paddingLeft: '20px',
    color: '#6E6254',
    fontSize: '14px',
    lineHeight: 1.75,

    li: {
      marginBottom: '8px',
    },
  },

  longTextBlock: {
    marginTop: '8px',
  },

  privacyHeading: {
    margin: '18px 0 8px',
    color: '#0F3D2E',
    fontSize: '15px',
    fontWeight: '900',
  },

  contactCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '20px',
    padding: '16px',
    marginTop: '14px',
    marginBottom: '16px',

    p: {
      margin: '0 0 10px',
      color: '#6E6254',
      fontSize: '14px',
      lineHeight: 1.6,
    },

    'p:last-child': {
      marginBottom: 0,
    },

    strong: {
      color: '#0F3D2E',
    },
  },

  faqItem: {
    padding: '14px 0',
    borderBottom: '1px solid #F1E7D2',

    h4: {
      margin: '0 0 6px',
      color: '#0F3D2E',
      fontSize: '15px',
      fontWeight: '900',
    },

    p: {
      margin: 0,
      color: '#6E6254',
      fontSize: '14px',
      lineHeight: 1.7,
    },

    ':last-child': {
      borderBottom: 'none',
    },
  },

  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
  },

  cancelButton: {
    flex: 1,
    border: '1px solid #E6D8BE',
    backgroundColor: '#F4EBD8',
    color: '#0F3D2E',
    borderRadius: '18px',
    padding: '13px',
    fontSize: '14px',
    fontWeight: '900',
    cursor: 'pointer',
  },

  confirmButton: {
    flex: 1,
    border: 'none',
    backgroundColor: '#D9534F',
    color: '#ffffff',
    borderRadius: '18px',
    padding: '13px',
    fontSize: '14px',
    fontWeight: '900',
    cursor: 'pointer',
  },

  closeModalButton: {
    width: '100%',
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    borderRadius: '18px',
    padding: '13px',
    fontSize: '14px',
    fontWeight: '900',
    cursor: 'pointer',
    marginTop: '18px',
  },
});

export default Profile;
